import warning from 'warning';
import getSectionsWithCounters from './sectionsWithCounters';

import type { SectionWithCounters, ListState, GridState, SectionProps } from './types';

type Props = {
  sections: SectionProps[],
};

const getGridItemIndex = (col: number, row: number, colCount: number) => row * colCount + col;

const findSectionForRow = (sections: SectionWithCounters[], rowIndex: number): ?SectionWithCounters =>
  sections.find((s) => rowIndex >= s.startRow && rowIndex <= s.endRow);

const findSectionForIndex = (sections: SectionWithCounters[], index: number): ?SectionWithCounters =>
  sections.find((s) => index >= s.startIndex && index <= s.endIndex);

const getLayoutDataFromProps = (props: Props, state: ListState | GridState, colCount: number) => {
  const counters = getSectionsWithCounters(props.sections, colCount),
    rowCount = Math.ceil(counters.total / colCount);

  warning(isFinite(rowCount), 'react-window-layout: Row Count is infinite! check your width allows at least one item');

  return {
    counters,
    rowCount,
  };
};

/**
 * returns the new state for List Layout in case sections data changed
 * returns null in case sections data didnt change from previous state
 */
const getListDataFromProps = (props: Props, state: ListState): ?ListState => {
  const data = getLayoutDataFromProps(props, state, 1);

  return (
    data && {
      total: data.counters.total,
      sections: data.counters.sections,
      rows: data.rowCount,
    }
  );
};

/**
 * returns the new state for Grid Layout in case sections data changed
 * returns null in case sections data didnt change from previous state
 */
const getGridDataFromProps = (props: Props & { itemWidth: number, width: number }, state: GridState): ?GridState => {
  const { itemWidth, width = 0 } = props,
    colCount = itemWidth ? Math.floor(width / itemWidth) : 0;

  const data = getLayoutDataFromProps(props, state, colCount);

  return (
    data && {
      total: data.counters.total,
      sections: data.counters.sections,
      rows: data.rowCount,
      cols: colCount,
    }
  );
};

const warnOnNoSection = (index: mixed, layoutType: string) => {
  warning(false, `section not found for [${index}] in ${layoutType}`);
};

/**
 * get index relative to the section
 */
const normalizeIndex = (index: number, section: SectionWithCounters) => index - section.startIndex;

export { getGridItemIndex, findSectionForRow, findSectionForIndex, getListDataFromProps, getGridDataFromProps, warnOnNoSection, normalizeIndex };
